import cn from 'classnames'
import React, { useCallback } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { useSelector } from 'react-redux'
import { matchPath } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import SvgAttachmentFile from 'src/components/Svgs/SvgAttachmentFile'
import { useTranslate } from 'src/localization/Trns'
import {
  createVirtualCardUrl,
  requestVirtualCardUrl,
} from 'src/navigation/urls'
import { selectIsPushingAttachmentsToERPFromState } from 'src/redux/selectors/data/receiptAttachments.selectors'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'

import style from './Dropzone.style'

interface DropzoneProps {
  className?: string
  uploadAttachments: (attachments: PostReceiptAttachmentRequest[]) => void
  isAttachmentsListEmpty?: boolean
  attachmentsText?: React.ReactElement
  isReceiptRequired?: boolean
  setAttachmentError?: (
    errorMsg: string | React.ReactElement | undefined,
  ) => void
  setErrors?: (onlyOneReceipt: boolean) => void
  attachmentLimit?: number
  attachmentsLength?: number
  testId?: string
}

const Dropzone: React.FC<DropzoneProps> = (props): React.ReactElement => {
  const translate = useTranslate()
  const {
    isAttachmentsListEmpty,
    attachmentsText,
    isReceiptRequired,
    uploadAttachments,
  } = props
  const location = useSelector(selectLocationFromState)
  const { analytics } = useAnalyticsContext()
  const isPushingToERP = useSelector(selectIsPushingAttachmentsToERPFromState)

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]): void => {
      props.setAttachmentError && props.setAttachmentError(undefined)

      if (
        props.attachmentLimit !== undefined &&
        props.attachmentsLength !== undefined &&
        props.attachmentsLength + acceptedFiles.length + fileRejections.length >
          props.attachmentLimit
      ) {
        props.setAttachmentError &&
          props.setAttachmentError(
            translate('Only ${numImages} images can be uploaded at a time', {
              numImages: props.attachmentLimit.toString(),
            }),
          )
        return
      }

      const attachments = acceptedFiles.map((file) => {
        const form = new FormData()
        form.append('file', file, file.name)
        const thumbnailUrl = URL.createObjectURL(file)
        return { formData: form, uploadId: thumbnailUrl, thumbnailUrl }
      })
      uploadAttachments(attachments)
      if (fileRejections.length > 0) {
        props.setAttachmentError &&
          props.setAttachmentError(translate('Files cannot exceed 10MB'))
      }
      if (props.setErrors !== undefined) {
        props.setErrors(false)
      }
      if (
        matchPath(location.pathname, requestVirtualCardUrl()) ||
        matchPath(location.pathname, createVirtualCardUrl())
      ) {
        analytics?.track(
          matchPath(location.pathname, requestVirtualCardUrl())
            ? 'request virtual card - upload attachment - button press'
            : 'create virtual card - upload attachment - button press',
          {
            screen: location.pathname,
            interaction: 'button press',
            intent: 'upload attachment',
          },
        )
      }
    },
    [uploadAttachments],
  )
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/tiff': ['.tiff', '.tif'],
      'image/gif': ['.gif'],
      'image/bmp': ['.bmp'],
      'image/heic': ['.heic'],
    },
    maxSize: 10000000,
  })

  return (
    <div
      className={cn('Dropdzone', props.className, {
        disabled: isPushingToERP,
        isEmpty: isAttachmentsListEmpty,
        isRequired: isReceiptRequired,
      })}
      css={style}
      data-testid={props.testId ?? 'add-attachment'}
      {...getRootProps()}
    >
      <input className={'dropzone-input'} {...getInputProps()} />
      {isAttachmentsListEmpty ? (
        attachmentsText !== undefined ? (
          attachmentsText
        ) : (
          <>
            <SvgAttachmentFile />
            <span>{translate('+ Add your documents here')}</span>
          </>
        )
      ) : (
        <span>{translate('+ Add')}</span>
      )}
    </div>
  )
}

export default Dropzone
